import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { Check, ChevronRight } from 'lucide-react';
import * as React from 'react';
import {
  usePortalContainerOverride,
  usePortalReplacementRef,
} from '@/components/ui/portal-container';
import { cn } from '@/lib/utils';

function DropdownMenuRoot({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu-root" {...props} />;
}
DropdownMenuRoot.displayName = 'DropdownMenu.Root';

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}
DropdownMenuPortal.displayName = 'DropdownMenu.Portal';

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}
DropdownMenuTrigger.displayName = 'DropdownMenu.Trigger';

function DropdownMenuPopup({
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  className,
  container,
  onKeyDownCapture,
  onKeyUpCapture,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<
    MenuPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  > & {
    /**
     * DOM node to portal the popup into. Falls back to
     * `usePortalContainerOverride()` (which checks `PortalContainerProvider`), then to
     * the base-ui default (the document body).
     */
    container?: HTMLElement | null;
  }) {
  const resolvedContainer = usePortalContainerOverride(container);
  const [popup, setPopup] = React.useState<HTMLDivElement | null>(null);
  const exitRef = React.useCallback<React.RefCallback<HTMLDivElement>>(
    (node) => {
      setPopup(node);
      const cleanup =
        typeof props.ref === 'function' ? props.ref(node) : undefined;
      if (props.ref && typeof props.ref !== 'function')
        props.ref.current = node;
      return () => {
        setPopup(null);
        if (cleanup) cleanup();
        else if (typeof props.ref === 'function') props.ref(null);
        else if (props.ref) props.ref.current = null;
      };
    },
    [props.ref],
  );
  React.useLayoutEffect(() => {
    if (!popup || (props.finalFocus !== undefined && props.finalFocus !== true))
      return;
    // Base UI retains popup focus until exit completes. Hand it back when the
    // close starts, so real keyboard input reaches the trigger, not stale items.
    // The owner-document observer also covers uncontrolled roots and submenus.
    const handoff = () => {
      if (
        popup.hasAttribute('data-closed') &&
        popup.contains(popup.ownerDocument.activeElement)
      ) {
        popup.ownerDocument
          .getElementById(popup.getAttribute('aria-labelledby') ?? '')
          ?.focus({ preventScroll: true });
      }
    };
    handoff();
    const Observer = popup.ownerDocument.defaultView?.MutationObserver;
    if (!Observer) return;
    const observer = new Observer(handoff);
    observer.observe(popup, {
      attributes: true,
      attributeFilter: ['data-closed'],
    });
    return () => observer.disconnect();
  }, [popup, props.finalFocus]);
  const { ref: popupRef, finalFocus } = usePortalReplacementRef(
    resolvedContainer,
    exitRef,
    undefined,
    props.finalFocus,
  );
  return (
    <MenuPrimitive.Portal container={resolvedContainer}>
      <MenuPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-popup"
          className={cn(
            'motion-safe:transition-opacity motion-safe:duration-(--duration-fast) motion-safe:ease-standard motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0 motion-reduce:transition-none ring-foreground/10 bg-popover text-popover-foreground min-w-32 rounded-md p-1 shadow-md ring-1 z-50 max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto outline-none data-closed:overflow-hidden',
            className,
          )}
          {...props}
          finalFocus={finalFocus}
          ref={popupRef}
          onKeyDownCapture={(event) => {
            onKeyDownCapture?.(event);
            if (!event.currentTarget.hasAttribute('data-closed')) return;
            // Cancel the original native activation, regardless of modifiers or
            // whether the trigger would cancel a forwarded event.
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            if (event.defaultPrevented) return;
            const trigger = event.currentTarget.ownerDocument.getElementById(
              event.currentTarget.getAttribute('aria-labelledby') ?? '',
            );
            const KeyboardEvent =
              event.currentTarget.ownerDocument.defaultView?.KeyboardEvent;
            if (!trigger || !KeyboardEvent) return;
            // Events explicitly sent to an outgoing item must not activate it.
            // Relay once to the trigger; Escape, Tab and global shortcuts retain
            // their normal bubbling and native default behavior.
            event.stopPropagation();
            const forwarded = new KeyboardEvent(event.type, {
              key: event.key,
              code: event.code,
              repeat: event.repeat,
              ctrlKey: event.ctrlKey,
              altKey: event.altKey,
              metaKey: event.metaKey,
              shiftKey: event.shiftKey,
              bubbles: true,
              cancelable: true,
            });
            if (!trigger.dispatchEvent(forwarded)) event.preventDefault();
          }}
          onKeyUpCapture={(event) => {
            onKeyUpCapture?.(event);
            if (
              event.currentTarget.hasAttribute('data-closed') &&
              event.key === ' '
            ) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}
DropdownMenuPopup.displayName = 'DropdownMenu.Popup';

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}
DropdownMenuGroup.displayName = 'DropdownMenu.Group';

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        'text-muted-foreground px-2 py-1.5 text-xs font-medium data-[inset]:ps-8',
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuLabel.displayName = 'DropdownMenu.Label';

function DropdownMenuItem({
  className,
  inset,
  variant = 'default',
  ...props
}: MenuPrimitive.Item.Props & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive not-data-[variant=destructive]:focus:**:text-accent-foreground gap-2 rounded-sm px-2 py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4 group/dropdown-menu-item relative flex cursor-default items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-[inset]:ps-8 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuItem.displayName = 'DropdownMenu.Item';

function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}
DropdownMenuSub.displayName = 'DropdownMenu.Sub';

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-open:bg-accent data-open:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground gap-2 rounded-sm px-2 py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4 flex cursor-default items-center outline-hidden select-none data-[inset]:ps-8 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ms-auto rtl:rotate-180" />
    </MenuPrimitive.SubmenuTrigger>
  );
}
DropdownMenuSubTrigger.displayName = 'DropdownMenu.SubTrigger';

function DropdownMenuSubPopup({
  align = 'start',
  alignOffset = -3,
  side = 'right',
  sideOffset = 0,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPopup>) {
  return (
    <DropdownMenuPopup
      data-slot="dropdown-menu-sub-popup"
      className={cn('min-w-[96px] shadow-lg w-auto', className)}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  );
}
DropdownMenuSubPopup.displayName = 'DropdownMenu.SubPopup';

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: MenuPrimitive.CheckboxItem.Props) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground gap-2 rounded-sm py-1.5 pe-8 ps-2 text-sm [&_svg:not([class*='size-'])]:size-4 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span
        className="pointer-events-none absolute end-2 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-(--duration-fast)">
          <Check />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}
DropdownMenuCheckboxItem.displayName = 'DropdownMenu.CheckboxItem';

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}
DropdownMenuRadioGroup.displayName = 'DropdownMenu.RadioGroup';

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground gap-2 rounded-sm py-1.5 pe-8 ps-2 text-sm [&_svg:not([class*='size-'])]:size-4 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute end-2 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-(--duration-fast)">
          <Check />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}
DropdownMenuRadioItem.displayName = 'DropdownMenu.RadioItem';

function DropdownMenuSeparator({
  className,
  ...props
}: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}
DropdownMenuSeparator.displayName = 'DropdownMenu.Separator';

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        'text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ms-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuShortcut.displayName = 'DropdownMenu.Shortcut';

// Compound export (Dot Notation Pattern)
export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Root: DropdownMenuRoot,
  Portal: DropdownMenuPortal,
  Trigger: DropdownMenuTrigger,
  Popup: DropdownMenuPopup,
  Group: DropdownMenuGroup,
  Label: DropdownMenuLabel,
  Item: DropdownMenuItem,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubPopup: DropdownMenuSubPopup,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
});
